import type {
	FormState,
	InputTextChangeEventDetail,
	InputTextInputEventDetail,
	PIconProps,
} from "@porsche-design-system/components-react"
import {
	PInputEmail,
	PInputPassword,
	PInputSearch,
	PInputText,
	PInputUrl,
	PText,
} from "@porsche-design-system/components-react"

import debounce from "lodash-es/debounce"
import type { FocusEventHandler, PropsWithChildren, Ref } from "react"
import { useCallback, useEffect, useMemo, useState } from "react"

export type InputValue = string | undefined

export interface InputProps {
	compact?: boolean
	debounced?: number
	description?: string
	disabled?: boolean
	errorMessage?: string
	hasCounter?: boolean
	hideLabel?: boolean
	icon?: PIconProps["name"]
	label?: string
	maxLength?: number
	minLength?: number
	name: string
	onChange?: (value: InputValue, event: CustomEvent<InputTextChangeEventDetail>) => void
	onFocus?: FocusEventHandler
	pattern?: RegExp
	placeholder?: string
	readOnly?: boolean
	ref?: Ref<HTMLInputElement>
	required?: boolean
	state?: FormState
	type: "text" | "password" | "search" | "email" | "url"
	unit?: string
	unitPosition?: "prefix" | "suffix"
	value?: InputValue
}

export const Input = (props: PropsWithChildren<InputProps>) => {
	const {
		children,
		compact,
		debounced,
		disabled,
		errorMessage,
		hasCounter,
		label,
		maxLength,
		minLength,
		name,
		onChange,
		onFocus,
		pattern,
		placeholder,
		readOnly,
		ref,
		required,
		state,
		type,
		unit,
		unitPosition,
		value,
		...rest
	} = props

	const [localValue, setLocalValue] = useState<InputValue>(value ?? "")
	const [hasPendingChanges, setHasPendingChanges] = useState(false)

	useEffect(() => {
		function updateLocalValue() {
			setLocalValue(value ?? "")
		}
		updateLocalValue()
	}, [value])

	const onChangeDebounced = useMemo(
		() =>
			debounce((newValue: InputValue, event: CustomEvent<InputTextChangeEventDetail>) => {
				setHasPendingChanges(false)
				onChange?.(newValue, event)
			}, debounced),
		[onChange, debounced, setHasPendingChanges],
	)

	const handleChangeDebounced = useCallback(
		(newValue: InputValue, event: CustomEvent<InputTextChangeEventDetail>) => {
			setHasPendingChanges(true)
			onChangeDebounced(newValue, event)
		},
		[onChangeDebounced],
	)

	const handleInput = useCallback(
		(event: CustomEvent<InputTextInputEventDetail>) => {
			const { value: newValue } = event.target as HTMLInputElement
			if (disabled || !onChange || (pattern && !pattern.test(newValue))) {
				return
			}
			setLocalValue(newValue)
			const val = newValue === "" ? undefined : newValue
			const callback = debounced ? handleChangeDebounced : onChange
			return callback(val, event)
		},
		[debounced, disabled, handleChangeDebounced, onChange, pattern],
	)

	const commonProps = {
		"data-testid": `input_${name}`,
		...rest,
		compact,
		disabled,
		label,
		maxLength,
		message: errorMessage && !hasPendingChanges ? errorMessage : undefined,
		minLength,
		name,
		onFocus,
		onInput: handleInput,
		placeholder,
		readOnly,
		ref,
		required,
		state: errorMessage && !hasPendingChanges ? "error" : state,
		value: localValue,
	}

	const unitSlot = unit ? (
		<PText slot={unitPosition === "suffix" ? "end" : "start"} color="contrast-medium" aria-hidden>
			{unit}
		</PText>
	) : null

	switch (type) {
		case "email":
			return (
				<PInputEmail {...commonProps} indicator>
					{unitSlot}
					{children}
				</PInputEmail>
			)
		case "url":
			return (
				<PInputUrl {...commonProps} indicator>
					{unitSlot}
					{children}
				</PInputUrl>
			)
		case "password":
			return <PInputPassword {...commonProps}>{unitSlot}</PInputPassword>
		case "search":
			return (
				<PInputSearch {...commonProps} indicator clear>
					{unitSlot}
					{children}
				</PInputSearch>
			)
		case "text":
			return (
				<PInputText {...commonProps} counter={hasCounter}>
					{unitSlot}
					{children}
				</PInputText>
			)
	}
}
